import {
  useBottomNavigator,
  useNavigateToTradesPage,
  useOnReconnect,
} from "utils/hooks/misc";
import TradePageProvider from "@shared/features/trades/common/TradesPageProvider";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FilterType,
  GetTradesRequest,
  LanguageNameSpaces,
  TradeData,
} from "@shared/types";
import TradesTableContent from "features/trades/TradesTableContent";
import TradeActionsProvider from "features/trades/OpenTrades/TradeActionsProvider";
import Button from "common/Buttons/Button";
import { OPEN_TRADES_PATH } from "common/constants";
import ExportOutlined from "@ant-design/icons/ExportOutlined";
import { BotDetailsContext } from "@shared/features/bots/BotDetails.context";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context";
import { displayResultMessage } from "utils/misc";
import { sharedLib } from "@shared/services/shared.service";
import { BacktestDetailsContext } from "@shared/features/bots/BacktestDetails.context";
import {
  getTradesFiltersSlice,
  getTradesFilterTypeToTitle,
} from "@shared/features/trades/common/utils.ts";
import FiltersChips from "common/Filters/FiltersChips/FiltersChips.tsx";
import MobileTradesCards from "features/trades/TradesCards/MobileTradesCards.tsx";
import styled from "styled-components";
import TradesPieChart from "features/trades/TradesCharts/TradesPieChart.tsx";
import ClosedTradesStatistics from "features/trades/ClosedTrades/ClosedTradesStatistics.tsx";
import { skipToken, SkipToken } from "@reduxjs/toolkit/query";
import { PageTabsContext } from "common/Tabs/PageTabs.context.ts";
import FiltersOpenClosedSwitch from "features/trades/common/FiltersOpenClosedSwitch.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  useDisplayTradeDetails,
  useSubUserPermissions,
} from "@shared/utils/hooks/misc.ts";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";

interface DrawerTradesPageProps {
  isDrawerOpen?: boolean;
  baseGetTradesArgs?: GetTradesRequest | SkipToken;
  isCardView?: boolean;
  openTradesData?: TradeData[];
  closedTradesData?: TradeData[];
  disableTradeClick?: boolean;
  hideFilterTypes?: FilterType[];
  tabKey?: string;
}

const DrawerTradesPage = ({
  isDrawerOpen,
  baseGetTradesArgs,
  isCardView,
  openTradesData,
  closedTradesData,
  disableTradeClick,
  hideFilterTypes,
  tabKey,
}: DrawerTradesPageProps) => {
  const [isOpenTrades, setIsOpenTrades] = useState(true);

  useEffect(() => setIsOpenTrades(true), [isDrawerOpen]);

  return (
    <TradePageProvider
      isOpenTrades={isOpenTrades}
      useOnReconnect={useOnReconnect}
      baseGetTradesArgs={baseGetTradesArgs}
      inDrawer={true}
    >
      <TradeActionsProvider>
        <DrawerTradesPageContent
          isOpenTrades={isOpenTrades}
          setIsOpenTrades={setIsOpenTrades}
          baseGetTradesArgs={baseGetTradesArgs}
          isCardView={isCardView}
          tradesData={isOpenTrades ? openTradesData : closedTradesData}
          disableTradeClick={disableTradeClick}
          hideFilterTypes={hideFilterTypes}
          tabKey={tabKey}
        />
      </TradeActionsProvider>
    </TradePageProvider>
  );
};

export default React.memo(DrawerTradesPage);

interface DrawerTradesPageContentProps {
  isOpenTrades: boolean;
  setIsOpenTrades: (isOpenTrades: boolean) => void;
  baseGetTradesArgs?: GetTradesRequest | SkipToken;
  isCardView?: boolean;
  tradesData?: TradeData[];
  disableTradeClick?: boolean;
  hideFilterTypes?: FilterType[];
  tabKey?: string;
}

const DrawerTradesPageContent = ({
  isOpenTrades,
  setIsOpenTrades,
  baseGetTradesArgs,
  isCardView,
  tradesData,
  disableTradeClick,
  hideFilterTypes,
  tabKey,
}: DrawerTradesPageContentProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { getOpenTradesQuery, getClosedTradesQuery, isBacktesting } =
    useContext(TradesPageContext)!;
  const { botId } = useContext(BotDetailsContext) || {};
  const { backtestingRun } = useContext(BacktestDetailsContext) || {};
  const { navigateWithQuery } = useNavigateToTradesPage();
  const [exportBacktestTrades, exportBacktestTradesStatus] =
    sharedLib.sharedApi.trades.useLazyExportBacktestTradesQuery();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { setTabCount } = useContext(PageTabsContext) || {};
  const isSkip = baseGetTradesArgs === skipToken;
  const { isReadOnly } = useSubUserPermissions();
  const displayTradeDetails = useDisplayTradeDetails();

  const numOpenTrades =
    tradesData && isOpenTrades
      ? tradesData.length
      : getOpenTradesQuery?.data?.data?.length;
  const numClosedTrades =
    tradesData && !isOpenTrades
      ? tradesData.length
      : getClosedTradesQuery?.data?.aggregated_data?.number_of_closed_trades;
  const numTrades = isOpenTrades ? numOpenTrades : numClosedTrades;

  useEffect(() => {
    if (tabKey) {
      setTabCount?.(tabKey, numTrades);
    }
  }, [numTrades]);

  const switchSelect = useMemo(
    () => (
      <FiltersOpenClosedSwitch
        isOpen={isOpenTrades}
        setIsOpen={setIsOpenTrades}
        numOpen={numOpenTrades}
        numClosed={numClosedTrades}
      />
    ),
    [numClosedTrades, numOpenTrades, isOpenTrades]
  );

  const onExportClick = () => {
    if (!backtestingRun) return;
    displayResultMessage(
      exportBacktestTrades({ backtesting_run_id: backtestingRun?.id }).unwrap()
    )
      .then((downloadLink) => {
        if (downloadLink) {
          window.location.href = downloadLink;
        }
      })
      .catch(() => {});
  };

  const getActionTrigger = () => {
    if (isSkip) return;
    if (botId && !baseGetTradesArgs?.is_backtesting) {
      return (
        <Button
          type="primary"
          onClick={() =>
            navigateWithQuery(OPEN_TRADES_PATH, {
              [FilterType.SOURCE_NAME]: botId,
            })
          }
          style={{ marginLeft: 8 }}
        >
          <ExportOutlined /> Trades Page
        </Button>
      );
    } else if (
      baseGetTradesArgs?.is_backtesting &&
      backtestingRun &&
      !IS_TELEGRAM_WEB_APP &&
      !isReadOnly
    ) {
      return (
        <Button
          $type="secondary"
          onClick={onExportClick}
          loading={exportBacktestTradesStatus.isFetching}
        >
          <ExportOutlined />
          {t("actions.export")}
        </Button>
      );
    }
  };

  const content = (
    <TradesTableContent
      allowTradeClick={
        !disableTradeClick &&
        !isSkip &&
        !!baseGetTradesArgs?.dca_bot_id &&
        displayTradeDetails
      }
      isOpenTrades={isOpenTrades}
      titleOrNode={switchSelect}
      actionTrigger={getActionTrigger()}
      isCardView={isCardView}
      data={tradesData}
    />
  );

  const filtersSlice = getTradesFiltersSlice(isOpenTrades, true);

  const filtersChips = useMemo(() => {
    return (
      <FiltersChips
        filtersSelectors={filtersSlice.selectors}
        filtersActions={filtersSlice.actions}
        filterTypeToTitle={getTradesFilterTypeToTitle(isOpenTrades)}
        extraComponent={switchSelect}
        hideFilterTypes={hideFilterTypes}
        hideFilters={isBacktesting}
      />
    );
  }, [
    filtersSlice,
    isOpenTrades,
    switchSelect,
    hideFilterTypes,
    isBacktesting,
  ]);

  const renderTopContainer = useCallback(() => {
    return isOpenTrades ? <TradesPieChart /> : <ClosedTradesStatistics />;
  }, [isOpenTrades]);

  if (isBottomNavigator) {
    return (
      <MobileWrapper>
        {filtersChips}
        <MobileTradesCards
          renderTopContainer={renderTopContainer}
          shouldFetchSocketPnl={!isBacktesting}
        />
      </MobileWrapper>
    );
  }

  return content;
};

const MobileWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;
